<template>
  <b-container class="h-100 page__checkout-learning-unit-option mb-5">
    <b-row v-if="learningUnitOptions.length" class="justify-content-center">
      <b-col align-self="start" class="col-12 col-lg-8">
        <b-col ref="text__cart-title" class="display-4 font-weight-bold mb-4 px-0">{{
          'Checkout'
        }}</b-col>

        <keep-alive>
          <Checkout
            ref="checkout"
            :learning-unit-options="learningUnitOptions"
            @update:selectedPayment="handleUpdate"
            @update:totalAmount="handleUpdateTotalAmount"
            @submit:receipt-form="handleUpdateReceiptForm"
          />
        </keep-alive>
      </b-col>
      <b-col class="mt-lg-u8 mt-5 col-12 col-lg-4">
        <div class="content__make-payment">
          <div class="mb-u4">
            <label class="label__total-amount"> Total :</label>
            <div ref="text__total-amount" class="text__total-price">
              <PriceDisplayer
                :normal-price="totalAmount"
                :discount-price="totalAmount"
                :is-uppercase="true"
                :is-show-normal-price="false"
                :is-currency-form="true"
              ></PriceDisplayer>
            </div>
          </div>
          <b-form-checkbox ref="checkbox__terms" v-model="isConsent" class="mb-u6">
            <router-link
              target="_blank"
              :to="{ name: 'payment-policy' }"
              style="text-decoration: underline"
            >
              Terms and Conditions
            </router-link>
          </b-form-checkbox>

          <b-button
            ref="button__make-payment"
            variant="success"
            class="button__make-payment mr-3 mx-auto w-100 d-flex justify-content-center"
            :disabled="isPayment || !isConsent"
            @click.prevent="onPayment"
          >
            <span class="d-flex align-items-center">
              <b-spinner v-if="isPayment" small class="mr-2"></b-spinner>
              Make Payment
            </span>
          </b-button>
        </div>
      </b-col>
    </b-row>

    <qr-payment-modal
      v-if="
        $_orderPaymentObserverMixin_order &&
        $_orderPaymentObserverMixin_order.status === 'PENDING' &&
        $_orderPaymentObserverMixin_order.latest_payment &&
        $_orderPaymentObserverMixin_order.latest_payment.payment_method === 'QR_PAYMENT'
      "
      ref="qrPaymentModal"
      :order="$_orderPaymentObserverMixin_order"
      :payment="$_orderPaymentObserverMixin_order.latest_payment"
    />
  </b-container>
</template>

<script>
import Checkout from '@/views/Cart/components/Checkout';
import QrPaymentModal from '@/components/Payment/QrPaymentModal.vue';

import LearningUnitOption from '@/models/LearningUnitOption';

import Order from '@/models/Order';
import OrderItem from '@/models/OrderItem';
import { datetimeMixin } from '@/mixins/datetimeMixin';
import { userInfoMixin } from '@/mixins/userInfoMixin';
import { pageNavigationMixin } from '@/mixins/pageNavigationMixin';
import { orderPaymentObserverMixin } from '@/mixins/orderPaymentObserverMixin';
import { formatPriceMixin } from '@/mixins/formatPriceMixin';
import PriceDisplayer from '@/views/LearningUnitExplore/components/PriceDisplayer';

export default {
  components: {
    Checkout,
    QrPaymentModal,
    PriceDisplayer
  },
  mixins: [
    datetimeMixin,
    userInfoMixin,
    pageNavigationMixin,
    orderPaymentObserverMixin,
    formatPriceMixin
  ],
  data() {
    return {
      isPayment: false,
      isConsent: false,
      paymentMethod: 'QR_PAYMENT',
      totalAmount: 0,
      receiptForm: {
        name: '',
        address: ''
      }
    };
  },
  computed: {
    learningUnitOptionsId() {
      return parseInt(this.$route.params.learningUnitOptionId);
    },
    learningUnitOptions() {
      return LearningUnitOption.query()
        .where('id', this.learningUnitOptionsId)
        .with('learning_unit')
        .get();
    }
  },
  created() {
    this.orderPaymentObserverMixin_event.addEventListener('order-paid', this.onOrderPaid);
  },

  async mounted() {
    this.isLoading = true;

    const option_id = this.learningUnitOptionsId;

    await LearningUnitOption.api().fetchOne(option_id);

    if (!this.learningUnitOptions.length) {
      this.$_pageNavigationMixin_gotoPreviousPage();
    }

    this.isLoading = false;
  },

  beforeDestroy() {
    this.orderPaymentObserverMixin_event.removeEventListener('order-paid', this.onOrderPaid);
  },

  methods: {
    handleUpdate(value) {
      this.paymentMethod = value;
    },

    handleUpdateTotalAmount(value) {
      this.totalAmount = value;
    },

    handleUpdateReceiptForm(value) {
      this.$set(this.receiptForm, 'name', value.name);
      this.$set(this.receiptForm, 'address', value.address);
    },

    async onPayment() {
      if (this.$refs['checkout'] && this.$refs['checkout'].$refs['receiptForm']) {
        this.$refs['checkout'].$refs['receiptForm'].submit();

        this.$refs['checkout'].$refs['receiptForm'].$v.$touch();

        if (this.$refs['checkout'].$refs['receiptForm'].$v.$anyError) {
          return this.$refs['checkout'].$refs['receiptForm'].$v.$anyError;
        }
      }

      let payload = {
        learning_unit_option_id: this.learningUnitOptionsId,
        payment_method: this.paymentMethod,
        cust_address: this.receiptForm.address,
        cust_name: this.receiptForm.name
      };

      try {
        this.isPayment = true;

        // call api make payment
        const order = await Order.api().checkout(payload);

        this.orderPaymentObserverMixin_orderId = order.id;

        await OrderItem.api().fetchItems({ order_id: this.$_orderPaymentObserverMixin_order.id });

        if (this.$_orderPaymentObserverMixin_order.status === 'SUCCESS') {
          const query = {};
          if (this.$route.query.previousPath) query.previousPath = this.$route.query.previousPath;
          if (this.$route.query.isUpgrade) query.isUpgrade = this.$route.query.isUpgrade;
          return this.$router.push({
            name: 'payment-success',
            params: { orderId: this.$_orderPaymentObserverMixin_order.id },
            query
          });
        }

        if (
          this.$_orderPaymentObserverMixin_order.status === 'PENDING' &&
          this.$_orderPaymentObserverMixin_order.payments.every(
            (payment) => payment.status === 'PENDING'
          )
        ) {
          this.$_orderPaymentObserverMixin_startOrderPulling();

          this.openModalPayment();
        }
      } catch (error) {
        this.$toast.open({
          message: 'Something went wrong, please try to make payment again.',
          type: 'error',
          duration: 5000
        });
      } finally {
        this.isPayment = false;
      }
    },
    openModalPayment() {
      if (!this.$_orderPaymentObserverMixin_order) return;
      if (!this.$_orderPaymentObserverMixin_order.latest_payment) return;

      switch (this.$_orderPaymentObserverMixin_order.latest_payment.payment_method) {
        case 'QR_PAYMENT':
          this.$nextTick(() => {
            if (this.$refs.qrPaymentModal) {
              this.$refs.qrPaymentModal?.open();
            }
          });

          break;

        default:
          break;
      }
    },
    onCancel() {
      this.isCheckout = false;
      this.isConsent = false;
      this.$_pageNavigationMixin_gotoPreviousPage();
    },

    onOrderPaid(event) {
      const query = {};
      if (this.$route.query.previousPath) query.previousPath = this.$route.query.previousPath;
      if (this.$route.query.isUpgrade) query.isUpgrade = this.$route.query.isUpgrade;
      this.$router.push({
        name: 'payment-success',
        params: { orderId: event.detail.id },
        query
      });
    }
    // async pullingOrder() {
    //   await Order.api().fetchOrder(this.orderId);

    //   if (
    //     this.order &&
    //     this.order.status === 'PENDING' &&
    //     this.order.latest_payment?.status === 'PENDING' &&
    //     this.$_datetimeMixin_isLessThanNow(new Date(this.order.latest_payment.due_date))
    //   ) {
    //     if (this.pullingOrderInterval) clearInterval(this.pullingOrderInterval);
    //     return;
    //   }

    //   if (
    //     this.order &&
    //     this.order.status === 'SUCCESS' &&
    //     (this.order.latest_payment?.status === 'SUCCESS' ||
    //       this.order.payments.some((payment) => payment.status === 'SUCCESS'))
    //   ) {
    //     if (this.pullingOrderInterval) clearInterval(this.pullingOrderInterval);

    //     this.$router.push({
    //       name: 'payment-success',
    //       params: { orderId: this.order.id }
    //     });
    //   }
    // }
  }
};
</script>

<style lang="scss">
.page__checkout-learning-unit-option {
  .content__make-payment {
    height: max-content;
    box-shadow: 0px 4px 8.8px 0px #a6a6b98c;
    border-radius: 1em;
    padding: 24px;
  }

  .text__total-price {
    font-size: 24px;
    font-weight: bold;

    strong * {
      margin: 0 !important;
    }
  }
}
</style>
